import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { LivenessFaceComparisonResult, LivenessSession } from '../../app/types';
import { fileToBase64 } from '../../lib/files';
import PageSection from '../shared/PageSection';

interface LivenessPageProps {
  readonly tenantId: string;
  readonly sessions: LivenessSession[];
  readonly onCreateSession: () => Promise<void>;
  readonly onRefreshSession: (sessionId: string) => Promise<void>;
  readonly onDeleteSession: (sessionId: string) => Promise<void>;
  readonly onCompareSessionFace: (
    sessionId: string,
    payload: { tenantId: string; sourceImageName: string; imageBase64: string; threshold?: number }
  ) => Promise<LivenessFaceComparisonResult>;
}

const MAX_IMAGE_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png']);

export default function LivenessPage({
  tenantId,
  sessions,
  onCreateSession,
  onRefreshSession,
  onDeleteSession,
  onCompareSessionFace,
}: LivenessPageProps) {
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [refreshingSessionId, setRefreshingSessionId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [compareSessionId, setCompareSessionId] = useState<string | null>(null);
  const [compareThreshold, setCompareThreshold] = useState('85');
  const [compareResult, setCompareResult] = useState<LivenessFaceComparisonResult | null>(null);
  const [compareError, setCompareError] = useState('');
  const [comparing, setComparing] = useState(false);
  const [copiedSessionId, setCopiedSessionId] = useState<string | null>(null);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  const isSessionClosed = (status: LivenessSession['status']) =>
    status === 'completed' || status === 'failed' || status === 'expired';
  const closedSessionLabel = (status: LivenessSession['status']) => {
    if (status === 'completed') {
      return 'Completed';
    }
    if (status === 'failed') {
      return 'Failed';
    }
    if (status === 'expired') {
      return 'Expired';
    }
    return 'Closed';
  };

  const create = async () => {
    setCreating(true);
    setError('');
    try {
      await onCreateSession();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Failed to create liveness session.');
    } finally {
      setCreating(false);
    }
  };

  const refresh = async (sessionId: string) => {
    setRefreshingSessionId(sessionId);
    setError('');
    try {
      await onRefreshSession(sessionId);
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : 'Failed to refresh liveness session.');
    } finally {
      setRefreshingSessionId(null);
    }
  };

  const openCompareDialog = (sessionId: string) => {
    setCompareSessionId(sessionId);
    setCompareThreshold('85');
    setCompareResult(null);
    setCompareError('');
  };

  const closeCompareDialog = () => {
    setCompareSessionId(null);
    setCompareThreshold('85');
    setCompareResult(null);
    setCompareError('');
    setComparing(false);
  };

  const uploadAndCompare = async (files: FileList | null) => {
    if (!compareSessionId) {
      return;
    }
    const file = files?.[0];
    if (!file) {
      return;
    }
    if (file.size > MAX_IMAGE_FILE_BYTES) {
      setCompareError('Image is too large. Maximum supported size is 5 MB.');
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      setCompareError('Unsupported image format. Please upload a JPEG or PNG image.');
      return;
    }

    try {
      setComparing(true);
      setCompareError('');
      const imageBase64 = await fileToBase64(file);
      const thresholdValue = Number(compareThreshold);
      const response = await onCompareSessionFace(compareSessionId, {
        tenantId,
        sourceImageName: file.name,
        imageBase64,
        threshold: Number.isFinite(thresholdValue) ? thresholdValue : undefined,
      });
      setCompareResult(response);
    } catch (compareRequestError) {
      setCompareError(compareRequestError instanceof Error ? compareRequestError.message : 'Face comparison failed.');
    } finally {
      setComparing(false);
    }
  };

  const copySessionLink = async (sessionId: string, link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedSessionId(sessionId);
      setError('');
      window.setTimeout(() => {
        setCopiedSessionId((current) => (current === sessionId ? null : current));
      }, 1800);
    } catch (copyError) {
      setError(copyError instanceof Error ? copyError.message : 'Failed to copy session link.');
    }
  };

  const deleteSession = async (sessionId: string) => {
    if (!window.confirm('Delete this liveness session, its result data, and associated images?')) {
      return;
    }

    try {
      setDeletingSessionId(sessionId);
      setError('');
      await onDeleteSession(sessionId);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Failed to delete liveness session.');
    } finally {
      setDeletingSessionId(null);
    }
  };

  return (
    <Stack spacing={3}>
      <PageSection title="Liveness" subtitle="Liveness sessions and captured reference images for this tenant.">
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="contained" onClick={() => void create()} disabled={creating}>
              {creating ? 'Creating...' : 'Create Liveness Session'}
            </Button>
            <Typography color="text.secondary">Tenant: {tenantId}</Typography>
          </Stack>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1260, width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Client Link</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => (
                <TableRow key={session.sessionId}>
                  <TableCell>{new Date(session.createdAt).toLocaleString()}</TableCell>
                  <TableCell><Chip size="small" label={session.status} /></TableCell>
                  <TableCell>{session.result ?? '-'}</TableCell>
                  <TableCell>{typeof session.confidence === 'number' ? `${session.confidence.toFixed(2)}%` : '-'}</TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={0.75} alignItems="flex-start">
                      {isSessionClosed(session.status) ? (
                        <Typography variant="body2" color="text.secondary">
                          {closedSessionLabel(session.status)}
                        </Typography>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          component="a"
                          href={session.clientSessionUrl}
                          target="_blank"
                          rel="opener"
                        >
                          Open session
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => void copySessionLink(session.sessionId, session.clientSessionUrl)}
                      >
                        {copiedSessionId === session.sessionId ? 'Copied' : 'Click to copy link'}
                      </Button>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {session.referenceImageUrl ? (
                      <img
                        src={session.referenceImageUrl}
                        alt="Liveness reference"
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
                        onClick={() => setPreviewUrl(session.referenceImageUrl ?? null)}
                      />
                    ) : (
                      'Not captured'
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={0.75} alignItems="flex-start">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => void refresh(session.sessionId)}
                        disabled={refreshingSessionId === session.sessionId}
                      >
                        {refreshingSessionId === session.sessionId ? 'Refreshing...' : 'Refresh'}
                      </Button>
                      {session.referenceImageKey ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openCompareDialog(session.sessionId)}
                          disabled={comparing && compareSessionId === session.sessionId}
                        >
                          Compare
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => void deleteSession(session.sessionId)}
                      disabled={deletingSessionId === session.sessionId}
                    >
                      {deletingSessionId === session.sessionId ? 'Deleting...' : 'Delete'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        </Stack>
      </PageSection>
      <Dialog open={Boolean(previewUrl)} onClose={() => setPreviewUrl(null)} maxWidth="md" fullWidth>
        <DialogTitle>Liveness Reference Image</DialogTitle>
        <DialogContent>
          {previewUrl ? (
            <img src={previewUrl} alt="Liveness reference" style={{ width: '100%', height: 'auto', borderRadius: 8 }} />
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(compareSessionId)} onClose={closeCompareDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Compare Uploaded Face</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {compareError ? <Alert severity="error">{compareError}</Alert> : null}
            <TextField
              label="Match threshold (%)"
              type="number"
              value={compareThreshold}
              onChange={(event) => setCompareThreshold(event.target.value)}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              size="small"
            />
            <Button variant="outlined" component="label" disabled={comparing}>
              {comparing ? 'Comparing...' : 'Upload Image & Compare'}
              <input
                hidden
                type="file"
                accept="image/jpeg,image/png"
                onChange={(event) => {
                  void uploadAndCompare(event.currentTarget.files);
                  event.currentTarget.value = '';
                }}
              />
            </Button>
            {compareResult ? (
              <Box sx={{ border: '1px solid #d0d7de', borderRadius: 1.5, p: 1.5, backgroundColor: '#f8fafc' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {compareResult.matched ? 'Match' : 'No match'}
                </Typography>
                <Typography variant="body2">Similarity: {compareResult.similarity.toFixed(2)}%</Typography>
                <Typography variant="body2">Threshold: {compareResult.threshold.toFixed(2)}%</Typography>
                <Typography variant="body2">Uploaded faces detected: {compareResult.sourceFaceCount}</Typography>
                <Typography variant="body2">Reference faces detected: {compareResult.targetFaceCount}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Compared at {new Date(compareResult.comparedAt).toLocaleString()}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCompareDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
